<template>
  <section class="ma-4 ma-md-8">
    <t-floating-bar v-if="eventFilter.resource?.value">
      {{ eventName(eventFilter.resource.value) }}
    </t-floating-bar>
    <t-page-title title="Vendas | Relatório de Compras" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :pagination="pagination"
          :actions="actions"
        >
          <template v-if="!loader.loading" #metadata>
            <v-row justify="end">
              <v-col md="6" lg="4">
                <v-table>
                  <thead>
                    <tr>
                      <th> Situação </th>
                      <th class="text-center">
                        Compras
                      </th>
                      <th class="text-center">
                        Ingressos
                      </th>
                      <th class="text-end">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in metadata" :key="index">
                      <td>
                        <v-chip :color="statusColor(item.status as string)">
                          {{ item.status }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        {{ item.orders }}
                      </td>
                      <td class="text-center">
                        {{ item.items }}
                      </td>
                      <td class="text-end">
                        {{ currency(item.value as number) }}
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-col>
            </v-row>
          </template>
          <template #[`item.order_number`]="{ item }">
            <span> {{ item?.order_number ?? "" }} </span>
          </template>
          <template #[`item.event.name`]="{ item }">
            <span> {{ item?.event?.title ?? "" }} </span>
          </template>
          <template #[`item.event.dates`]="{ item }">
            <span>
              {{
                fromSQL(item?.event?.dates?.start, 'dd/MM/yyyy HH:mm')
              }}
            </span>
          </template>
          <template #[`item.owner_name`]="{ item }">
            <span>
              {{ item?.owner?.name ?? "--" }}
              <br>
              <span class="text-caption">{{ item?.owner?.document ? $formatters.document(item.owner.document, item.owner.document_type) : "" }}</span>
            </span>
          </template>
          <template #[`item.payment_method`]="{ item }">
            <span v-if="item.payment_method">
              {{ item.payment_method.name }}
            </span>
            <span v-else>--</span>
          </template>
          <template #[`item.total_value`]="{ item }">
            <span>
              {{ $formatters.currency(parseFloat(item.total_value)) }}
            </span>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip :color="statusColor(item.status)">
              {{ item.status }}
            </v-chip>
          </template>
          <template #[`item.created_at`]="{ item }">
            <span>
              {{
                $luxon.DateTime.fromSQL(item.created_at).toFormat(
                  "dd/MM/yyyy HH:mm:ss"
                )
              }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { Ref, ref } from 'vue'
import TPageTitle from '@/components/TPageTitle.vue'
import TFloatingBar from '@/components/TFloatingBar.vue'
import OrderService from '@/services/OrderService'
import type {
  DataTableHeader,
  DataTablePagination,
  DataTableFilter,
  DefaultDataTableActions,
} from '@/types/DataTable'

import { useSnackBar } from '@/stores/snackbar'
import { useProducerStore } from '@/stores/producer'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { usePaymentMethodFilter } from '@/helpers/reports/paymentMethodFilter'
import { usePaymentStatusFilter } from '@/helpers/reports/paymentStatusFilter'
import DataTable from '@/components/datatable/DataTable.vue'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useDateIntervalFilter } from '@/helpers/reports/dateIntervalFilter'
import { eventName } from '@/plugins/eventName'
import Order from '@/types/Order'
import { currency } from '@/helpers/formatters/currency'
import { useAbility } from '@casl/vue'
import { useLoader } from '@/stores/loader'
import { fromSQL } from '@/plugins/date'
import { useSelectFilter } from '@/helpers/reports/selectFilter'

const { can } = useAbility()
const snackbar = useSnackBar()
const loader = useLoader()

const headers: DataTableHeader[] = [
  { title: 'Número da compra', value: 'order_number', key: 'order_number', sortable: false },
  { title: 'Evento', value: 'event.name', key: 'event.name', sortable: false },
  { title: 'Data do Evento', value: 'event.dates', key: 'event.dates', sortable: false, align: 'center' },
  { title: 'Comprador', value: 'owner.name', key: 'owner_name', sortable: false },
  { title: 'Forma de pagamento', value: 'payment_method.name', key: 'payment_method', sortable: false },
  { title: 'Valor Total', value: 'total_value', key: 'total_value', sortable: false, align: 'center' },
  { title: 'Ingressos', value: 'total_items', key: 'total_items', sortable: false, align: 'center' },
  { title: 'Status', value: 'status', key: 'status', sortable: false, align: 'center' },
  { title: 'Data da venda', value: 'created_at', key: 'created_at', sortable: false, align: 'center' },
  { key: 'actions', value: '', title: 'Ações', sortable: false, align: 'center' },
]

const eventFilter = useEventFilter({
  producer: useProducerStore().producer!,
  field: 'event_id',
  type: 'autocomplete',
  label: 'Evento',
})

const filters: Ref<DataTableFilter[]> = ref([
  eventFilter,
  usePaymentMethodFilter({ label: 'Método de Pagamento', field: 'payment_method', multiple: false }),
  usePaymentStatusFilter({ label: 'Status do Pagamento', field: 'status', multiple: false }),
  useTextFilter({ label: 'Número da Compra (IDV)', field: 'order_number', required: false }),
  useTextFilter({ label: 'Código do Ingresso', field: 'items.code', required: false }),
  useTextFilter({ label: 'Nome do Comprador', field: 'buyer_name', required: false }),
  useTextFilter({ label: 'Documento do Comprador', field: 'buyer_document', required: false }),
  useTextFilter({ label: 'E-mail do comprador', field: 'buyer_email', required: false }),
  useDateIntervalFilter({
    field: 'date',
    label: 'Período',
    required: false,
    initial: null,
  }),
  useSelectFilter({
    field: 'chargebacked',
    label: 'Chargeback',
    icon: 'fa-solid fa-list',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Sem Chargeback', value: '0' },
      { title: 'Com Chargeback', value: '1' },
    ],
    required: false,
    initial: '',
  }),
])

const pagination: Ref<DataTablePagination> = ref({
  page: 1,
  itemsPerPage: 50,
  total: 0,
  totalPages: 0,
})

const metadata = ref<Record<string, unknown>[]>()

const actions: DefaultDataTableActions<Order> = {
  read: {
    callback: (item: Order) => window.open(`/reports/orders/${item.id}`, '_blank'),
    condition: () => can('read', 'sales.report.orders'),
  },
}

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    const response = await new OrderService().index(
      page,
      filters,
      per_page,
    )

    metadata.value = response.data.meta.additional as Record<string, unknown>[]

    return response
  } catch (error) {
    snackbar.show('Não foi possível buscar as compras', 'error')

    throw error
  }
}

function statusColor(status: string): string {
  const candidates: Record<string, string> = {
    'Confirmado': 'green',
    'Cancelado': 'red',
    'Recusado': 'orange',
    'Aguardando Pagamento': 'blue',
    'Cortesia': 'grey',
  }

  return candidates[status] ?? 'grey'
}

</script>
